import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Rating,
  Stack,
  MenuItem,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { is } from 'date-fns/locale';

const categories = ['All', 'Dress', 'Mobile', 'Shoes', 'Electronics', 'Books'];
const genders = ['All', 'Male', 'Female', 'Unisex'];
const ratings = [0, 1, 2, 3, 4, 5];

const MyProductList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [gender, setGender] = useState('All');
  const [minRating, setMinRating] = useState(0);
  const [products, setProducts] = useState([]);
  const [likedProducts, setLikedProducts] = useState([]);

  const resellerId = localStorage.getItem('resellerId');
  const username = localStorage.getItem('username');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();

  useEffect(() => {
    if (!resellerId) return;

    fetch(`https://reviewhub-lc.onrender.com/api/reviews/reseller/${resellerId}`)
      .then((res) => res.json())
      .then((data) => setProducts(data))
      .catch((err) => console.error('Error fetching products:', err));

    if (username) {
      fetch('https://reviewhub-lc.onrender.com/api/auth/get-likes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data.likes)) {
            setLikedProducts(data.likes);
          }
        })
        .catch((err) => console.error('Error fetching liked products:', err));
    }
  }, [resellerId, username]);

  const toggleLike = async (productId) => {
    if (!username) {
      alert('Please login to like products');
      return;
    }

    const isLiked = likedProducts.includes(productId);
    const updatedLikes = isLiked
      ? likedProducts.filter(id => id !== productId)
      : [...likedProducts, productId];

    setLikedProducts(updatedLikes);
    localStorage.setItem('likedProducts', JSON.stringify(updatedLikes));

    try {
      await fetch(`https://reviewhub-lc.onrender.com/api/auth/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, productId, liked: !isLiked }),
      });
    } catch (err) {
      console.error('Error updating like:', err);
    }
  };

  const handleAddToCart = async (product) => {
    if (!username) {
      alert('Please login to add to cart');
      return;
    }

    try {
      await fetch('https://reviewhub-lc.onrender.com/api/auth/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, productId: product._id }),
      });
      alert('Product added to cart!');
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  const filtered = products.filter((p) => {
    const nameMatch = p.productName?.toLowerCase().includes(searchTerm.toLowerCase());
    const categoryMatch = category === 'All' || p.category === category;
    const genderMatch = gender === 'All' || p.gender === gender;
    const ratingMatch = p.rating >= minRating;
    return nameMatch && categoryMatch && genderMatch && ratingMatch;
  });

  return (
    <Box sx={{ p: 1, maxWidth: '1300px', mx: 'auto' }}>
      {/* Filters from old MyProductList */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search by name"
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
                <Grid item xs={6} sm={4} md={2}>
          <TextField
            select
            label="Min Rating"
            value={minRating}
            onChange={(e) => setMinRating(Number(e.target.value))}
            fullWidth
          >
            {ratings.map((r) => (
              <MenuItem key={r} value={r}>
                {r}+
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            select
            label="Category"
            value={category}
            sx={{ mb: isMobile ? 0 : 2,width: isMobile ? 140 : 150 }}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            select
            label="Gender"
            value={gender}
                      sx={{ mb: isMobile ? 0 : 2,width: isMobile ? 140 : 150 }}
            onChange={(e) => setGender(e.target.value)}
            fullWidth
          >
            {genders.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

      </Grid>

      {/* Cards from ProductList */}
      <Grid container spacing={isMobile ? 1 : 2}>
        {filtered.map((product) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={3}
            key={product._id}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: isMobile ? 145 : 220,
              minWidth: isMobile ? 145 : 220,
              maxWidth: isMobile ? 145 : 220,
            }}
          >
            <Card
              sx={{
                width: isMobile ? 145 : 220,
                maxWidth: isMobile ? 145 : 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: isMobile ? 340 : '100%',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                },
              }}
            >
              <Box sx={{ position: 'relative', width: '100%', pt: '100%', overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  image={`https://reviewhub-lc.onrender.com/${product.productMainImage.replace(/\\/g, '/')}`}
                  alt={product.productName}
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#fff'
                  }}
                />

                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: likedProducts.includes(product._id) ? 'red' : 'rgba(0,0,0,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    p: 0.5,
                  }}
                  onClick={() => toggleLike(product._id)}
                >
                  {likedProducts.includes(product._id)
                    ? <FavoriteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    : <FavoriteBorderIcon fontSize={isMobile ? 'small' : 'medium'} />}
                </IconButton>

                {product.platform && (
                  <Chip
                    label={product.platform}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      bgcolor:
                        product.platform === 'Flipkart' ? '#2874f0' :
                          product.platform === 'Meesho' ? '#9c27b0' :
                            product.platform === 'Myntra' ? '#ff3f6c' :
                              product.platform === 'Amazon' ? '#ff9900' :
                                product.platform === 'Instagram' ? '#e1306c' : 'grey',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}

                {product.discount && (
                  <Chip
                    label={`${product.discount}% OFF`}
                    color="error"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: product.platform ? 36 : 4,
                      left: 4,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>

              <CardContent sx={{
                flexGrow: 1,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: isMobile ? 100 : 150,
              }}>
                <Typography
                  variant={isMobile ? 'body2' : 'subtitle2'}
                  fontWeight="bold"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    minHeight: 30,
                    maxWidth: '100%',
                  }}
                >
                  {product.productName}
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    label={`${product.rating}★`}
                    size="small"
                    sx={{
                      bgcolor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '10px' : '12px',
                      height: isMobile ? 15 : 18,
                      px: 1,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewsCount || 0})
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight="bold">
                    ₹{product.price}
                  </Typography>
                  {product.originalPrice && (
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ₹{product.originalPrice}
                    </Typography>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                    gap: 0.5,
                    ...(isMobile && { minHeight: 36 }),
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={!isMobile && <ShoppingCartIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flex: 1,
                      minHeight: isMobile ? 36 : undefined,
                      fontSize: isMobile ? '10px' : '12px',
                      textTransform: 'none',
                    }}
                    onClick={() => handleAddToCart(product)}
                  >
                    {isMobile ? 'Cart' : 'Add to Cart'}
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      flex: 1,
                      minHeight: isMobile ? 36 : undefined,
                      fontSize: isMobile ? '10px' : '12px',
                      textTransform: 'none',
                      backgroundColor: '#ff3f6c',
                      '&:hover': {
                        backgroundColor: '#e6395f',
                      },
                    }}
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    View
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default MyProductList;
